

import { WidgetErrorBoundary } from "./widgets/WidgetErrorBoundary";
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { Layout } from "react-grid-layout";
import { Responsive, WidthProvider } from "react-grid-layout";
import { useQuery } from "@tanstack/react-query";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "../styles/drag-drop.css";
import "../styles/dashboard-theming.css";
import "../../grid/styles/sheet.css";

const ResponsiveGrid = WidthProvider(Responsive);
import type { Dashboard } from "../services/dashboard-api.service";
import { dashboardService } from "../services/dashboard-api.service";
import { WidgetLibrarySidebar } from "./WidgetLibrarySidebar";
import { WidgetConfigSidebar } from "./WidgetConfigSidebar";
import { TabBar } from "../../grid/components/TabBar";
import { EmptyCanvasState } from "./EmptyCanvasState";
import { MetricWidget } from "./widgets/MetricWidget";
import { ChartWidget } from "./widgets/ChartWidget";
import { TextWidget } from "./widgets/TextWidget";
import { MediaWidget } from "./widgets/MediaWidget";
import { IFrameWidget } from "./widgets/IFrameWidget";
import { TableWidget } from "./widgets/TableWidget";
import { useToolbar } from "../contexts/ToolbarContext";
import { useSaveStatus } from "../contexts/SaveStatusContext";
import { SaveIndicator } from "./SaveIndicator";
import {
  ArrowLeft,
  Eye,
  Settings as SettingsIcon,
  X,
  GripVertical,
  Copy,
  Download,
  Edit2,
  TrendingUp,
} from "lucide-react";
import axios from "axios";
import { ExportModal } from "./ExportModal";
import { RenameDashboardModal } from "./RenameDashboardModal";
import { usePresentMode } from "../contexts/PresentModeContext"; // Story 2.21
import { DashboardSettingsModal } from "./DashboardSettingsModal"; // Story 2.21
import { PresentModeNavigation } from "./PresentModeNavigation";
import { useDashboardSettings } from "../contexts/DashboardSettingsContext"; // Story 2.21

// Default widget sizes (Story 2.11)
const DEFAULT_WIDGET_SIZES: Record<
  string,
  { w: number; h: number; minW: number; minH: number }
> = {
  metric: { w: 3, h: 4, minW: 2, minH: 2 },
  chart: { w: 6, h: 8, minW: 4, minH: 4 },
  text: { w: 4, h: 3, minW: 2, minH: 1 },
  table: { w: 6, h: 6, minW: 4, minH: 4 },
  media: { w: 4, h: 4, minW: 3, minH: 3 },
  iframe: { w: 8, h: 6, minW: 4, minH: 4 },
};

// Custom widgets registry
const CUSTOM_WIDGETS: any[] = [];

type SidebarMode =
  | { type: "library" }
  | { type: "config"; widgetId: string; widgetType: string };

// Calculate required canvas height per page (Story 2.175)
function calculatePageCanvasHeight(widgets: any[]): number {
  if (widgets.length === 0) return 24; // Default for empty page

  const maxBottomRow = Math.max(...widgets.map((w: any) => w.y + w.h));

  // No extra buffer - exact height to highest widget, minimum 24 rows
  return Math.max(24, maxBottomRow);
}

interface DashboardCanvasProps {
  /** Optional dashboard ID - if not provided, uses URL params */
  dashboardId?: string;
  /** Optional module ID for module context */
  moduleId?: string;
}

export function DashboardCanvas(props: DashboardCanvasProps = {}) {
  const params = useParams<{ id: string }>();
  const dashboardId = props.dashboardId ?? params.id;
  const navigate = useNavigate();
  const { setCustomComponent } = useToolbar();
  const { saveStatus, markSaving, markSaved, markError } = useSaveStatus();
  const { togglePresenting, isPresenting } = usePresentMode(); // Story 2.21
  const { getCurrentSettings, applySettingsToDOM } = useDashboardSettings(); // Story 2.21
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>({
    type: "library",
  });
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [rowHeight, setRowHeight] = useState(30);
  const [draggingWidget, setDraggingWidget] = useState<{
    type: string;
    w: number;
    h: number;
  } | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [hideToolbar, setHideToolbar] = useState<Record<string, boolean>>({});
  const [canvasHeight, setCanvasHeight] = useState(24); // Story 2.175: Dynamic canvas height
  const [showExportModal, setShowExportModal] = useState(false); // Story 2.19: Export modal
  const [showSettingsModal, setShowSettingsModal] = useState(false); // Story 2.21: Settings modal
  const [showRenameModal, setShowRenameModal] = useState(false); // Rename dashboard modal
  const [widgetSpacing, setWidgetSpacing] = useState(8); // Story 2.21: Widget spacing from settings
  const [showGridlines, setShowGridlines] = useState(false); // Story 2.21: Gridlines from settings

  // Calculate row height to fit exactly 24 rows in viewport
  useEffect(() => {
    const calculateRowHeight = () => {
      const viewportHeight = window.innerHeight;

      let fixedOverhead;

      if (isPresenting) {
        // Present mode: Full viewport, only canvas padding and gutters
        const canvasPadding = 32; // p-4 on canvas div (16px top + 16px bottom)
        const gutterTotal = widgetSpacing * 23; // 23 gaps between 24 rows
        fixedOverhead = canvasPadding + gutterTotal;
      } else {
        // Normal mode: Shell chrome + page tabs
        const header = 48; // h-12 (Header.tsx)
        const toolbar = 48; // h-12 (Shell.tsx custom toolbar area)
        const footer = 32; // h-8 (Footer.tsx)
        const pageTabsArea = 60; // Page tabs with py-3 + border
        const canvasPadding = 32; // p-4 on canvas div (16px top + 16px bottom)
        const gutterTotal = widgetSpacing * 23; // 23 gaps between 24 rows
        fixedOverhead =
          header +
          toolbar +
          footer +
          pageTabsArea +
          canvasPadding +
          gutterTotal;
      }

      // Remaining space for 24 rows
      const availableForRows = viewportHeight - fixedOverhead;
      const calculatedRowHeight = Math.floor(availableForRows / 24);

      setRowHeight(Math.max(calculatedRowHeight, 15)); // Minimum 15px per row
    };

    calculateRowHeight();
    window.addEventListener("resize", calculateRowHeight);
    return () => window.removeEventListener("resize", calculateRowHeight);
  }, [isPresenting, widgetSpacing]); // Recalculate when present mode or spacing changes

  // Clear custom toolbar - we'll render our own toolbar within the component
  useEffect(() => {
    setCustomComponent(null);
    return () => setCustomComponent(null);
  }, [setCustomComponent]);

  // Load dashboard with React Query to handle domain switching invalidation
  const {
    data: fetchedDashboard,
    isError,
    error,
    isLoading: isQueryLoading,
  } = useQuery({
    queryKey: ["dashboard", dashboardId],
    queryFn: () => dashboardService.get(dashboardId!),
    enabled: !!dashboardId,
    retry: false,
  });

  // Sync fetched data to local state
  useEffect(() => {
    if (fetchedDashboard) {
      setDashboard(fetchedDashboard);
      setLoading(false);
    }
  }, [fetchedDashboard]);

  // Handle errors (e.g. 404 on domain switch)
  useEffect(() => {
    if (isError) {
      console.error("Failed to load dashboard:", error);
      navigate("/dashboards");
    }
  }, [isError, error, navigate]);

  // Update loading state based on query
  useEffect(() => {
    if (isQueryLoading) {
      setLoading(true);
    }
  }, [isQueryLoading]);

  // Apply dashboard settings when dashboard loads or changes (Story 2.21)
  useEffect(() => {
    if (!dashboardId) return;

    const settings = getCurrentSettings(dashboardId);
    applySettingsToDOM(settings);
    setWidgetSpacing(settings.widgetSpacing / 2); // Divide by 2 since margin is [x, y] and we want total gap
    setShowGridlines(settings.showGridlines);

    // Set row height CSS variable for gridlines
    document.documentElement.style.setProperty(
      "--dashboard-row-height",
      `${rowHeight + widgetSpacing}px`
    );
  }, [
    dashboardId,
    getCurrentSettings,
    applySettingsToDOM,
    rowHeight,
    widgetSpacing,
  ]);

  // Get current page
  const currentPage = dashboard?.pages?.[currentPageIndex];
  const widgets = currentPage?.widgets || [];

  // Calculate canvas height when widgets or page changes (Story 2.175)
  useEffect(() => {
    const newHeight = calculatePageCanvasHeight(widgets);
    setCanvasHeight(newHeight);
  }, [widgets, currentPageIndex]);

  // Present Mode keyboard navigation (Story 2.21)
  useEffect(() => {
    if (!isPresenting || !dashboard) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "ArrowRight" &&
        currentPageIndex < dashboard.pages.length - 1
      ) {
        e.preventDefault();
        setCurrentPageIndex(currentPageIndex + 1);
      } else if (e.key === "ArrowLeft" && currentPageIndex > 0) {
        e.preventDefault();
        setCurrentPageIndex(currentPageIndex - 1);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isPresenting, currentPageIndex, dashboard]);

  // Convert widgets to react-grid-layout format
  const layout: Layout[] = widgets.map((w) => ({
    i: w.id,
    x: w.x,
    y: w.y,
    w: w.w,
    h: w.h,
    minW: w.minW,
    minH: w.minH,
    static: false, // Allow moving/resizing
  }));

  // Handle layout change (drag/resize) - Debounced auto-save (Story 2.175: Unlimited rows)
  const handleLayoutChange = (newLayout: Layout[]) => {
    if (!currentPage || widgets.length === 0) return;

    // Story 2.175: No row limit - allow unlimited vertical expansion
    const constrainedLayout = newLayout;

    // Clear existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    // Mark as saving (will show indicator)
    markSaving();

    // Set new timeout (save after 2.5 seconds of inactivity)
    const timeout = setTimeout(async () => {
      const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5173";

      try {
        // Update each widget's position/size if changed
        for (const item of constrainedLayout) {
          const widget = widgets.find((w) => w.id === item.i);
          if (!widget) continue;

          // Check if position or size changed
          if (
            widget.x !== item.x ||
            widget.y !== item.y ||
            widget.w !== item.w ||
            widget.h !== item.h
          ) {
            await axios.put(`${API_URL}/api/shell/widgets/${widget.id}`, {
              x: item.x,
              y: item.y,
              w: item.w,
              h: item.h,
            });
          }
        }

        // Refresh dashboard to get updated data
        const updated = await dashboardService.get(dashboardId!);
        setDashboard(updated);

        // Mark as saved
        markSaved();
      } catch (error: any) {
        console.error("Failed to save widget position:", error);
        markError(error.message || "Failed to save widget position");
      }
    }, 2500);

    setSaveTimeout(timeout);
  };

  // Find next available position for widget (Story 2.175: Supports unlimited rows)
  const findNextPosition = (
    widgetType: string,
    customSize?: { w: number; h: number }
  ): { x: number; y: number } | null => {
    const size =
      customSize ||
      DEFAULT_WIDGET_SIZES[widgetType] ||
      DEFAULT_WIDGET_SIZES.metric;

    // If no widgets, place at 0,0
    if (widgets.length === 0) {
      return { x: 0, y: 0 };
    }

    // Find the lowest Y position with available space
    // Story 2.175: Search up to current canvas height + 50 rows for placement
    const maxSearchY = canvasHeight + 50;

    for (let y = 0; y <= maxSearchY; y++) {
      for (let x = 0; x <= 12 - size.w; x++) {
        // Check if this position overlaps any existing widget
        const overlaps = widgets.some((w) => {
          const wouldOverlap = !(
            (
              x + size.w <= w.x || // New widget ends before existing starts (left)
              x >= w.x + w.w || // New widget starts after existing ends (right)
              y + size.h <= w.y || // New widget ends before existing starts (top)
              y >= w.y + w.h
            ) // New widget starts after existing ends (bottom)
          );
          return wouldOverlap;
        });

        if (!overlaps) {
          return { x, y };
        }
      }
    }

    // No space found
    return null;
  };

  // Handle widget drop from sidebar (Story 2.11)
  const handleWidgetDrop = async (
    widgetType: string,
    dropX?: number,
    dropY?: number
  ) => {
    if (!currentPage) return;

    const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5173";
    const size =
      DEFAULT_WIDGET_SIZES[widgetType] || DEFAULT_WIDGET_SIZES.metric;

    // Use provided position or find next available
    let position: { x: number; y: number } | null;

    if (dropX !== undefined && dropY !== undefined) {
      position = { x: dropX, y: dropY };
    } else {
      position = findNextPosition(widgetType);

      // Check if page is full
      if (!position) {
        alert(
          `No room on this page for a ${widgetType} widget (${size.w}×${size.h}). Try adding a new page or removing some widgets.`
        );
        return;
      }
    }

    try {
      // Create widget via API
      const response = await axios.post(
        `${API_URL}/api/shell/pages/${currentPage.id}/widgets`,
        {
          widgetType,
          x: position.x,
          y: position.y,
          w: size.w,
          h: size.h,
          minW: size.minW,
          minH: size.minH,
          config: {
            title: `New ${
              widgetType.charAt(0).toUpperCase() + widgetType.slice(1)
            } Widget`,
            // Placeholder config - will be replaced in widget stories
          },
        }
      );

      // Refresh dashboard
      const updated = await dashboardService.get(dashboardId!);
      setDashboard(updated);

      // Switch sidebar to config mode
      setSidebarMode({
        type: "config",
        widgetId: response.data.id,
        widgetType,
      });
      setSelectedWidgetId(response.data.id);
    } catch (error: any) {
      console.error("Failed to create widget:", error);
      alert(
        `Failed to add widget: ${error.response?.data?.error || error.message}`
      );
    }
  };

  // Handle widget click (select for editing)
  const handleWidgetClick = (widgetId: string, widgetType: string) => {
    setSelectedWidgetId(widgetId);
    setSidebarMode({ type: "config", widgetId, widgetType });
  };

  // Handle canvas click (deselect)
  const handleCanvasClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Deselect if clicking canvas area (not a widget)
    if (
      target.classList.contains("dashboard-grid") ||
      target.closest(".p-4.relative") === e.currentTarget
    ) {
      setSelectedWidgetId(null);
      setSidebarMode({ type: "library" });
    }
  };

  // Handle widget delete
  const handleWidgetDelete = async () => {
    if (sidebarMode.type !== "config") return;

    const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5173";

    try {
      await axios.delete(
        `${API_URL}/api/shell/widgets/${sidebarMode.widgetId}`
      );

      // Refresh dashboard
      const updated = await dashboardService.get(dashboardId!);
      setDashboard(updated);

      // Return to library mode
      setSidebarMode({ type: "library" });
      setSelectedWidgetId(null);
    } catch (error: any) {
      console.error("Failed to delete widget:", error);
      alert("Failed to delete widget");
    }
  };

  // Handle config change (update local state immediately, save debounced)
  const [configSaveTimeout, setConfigSaveTimeout] =
    useState<NodeJS.Timeout | null>(null);

  const handleConfigChange = (config: any) => {
    if (sidebarMode.type !== "config" || !dashboard) return;

    // Update local state immediately for live preview
    const updatedDashboard = {
      ...dashboard,
      pages: dashboard.pages.map((page) =>
        page.id === currentPage?.id
          ? {
              ...page,
              widgets: page.widgets.map((w) =>
                w.id === sidebarMode.widgetId
                  ? { ...w, config: JSON.stringify(config) }
                  : w
              ),
            }
          : page
      ),
    };
    setDashboard(updatedDashboard);

    // Clear existing timeout
    if (configSaveTimeout) {
      clearTimeout(configSaveTimeout);
    }

    // Mark as saving
    markSaving();

    // Debounce save to API (2.5 seconds)
    const timeout = setTimeout(async () => {
      const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5173";

      try {
        await axios.put(
          `${API_URL}/api/shell/widgets/${sidebarMode.widgetId}`,
          {
            config,
          }
        );
        markSaved();
      } catch (error: any) {
        console.error("Failed to save config:", error);
        markError(error.message || "Failed to save widget config");
      }
    }, 2500);

    setConfigSaveTimeout(timeout);
  };

  // Handle config done
  const handleConfigDone = () => {
    setSidebarMode({ type: "library" });
    setSelectedWidgetId(null);
  };

  // Add page
  const handleAddPage = async () => {
    if (!dashboardId) return;

    try {
      await dashboardService.addPage(dashboardId);
      const updated = await dashboardService.get(dashboardId);
      setDashboard(updated);
      setCurrentPageIndex(updated.pages.length - 1); // Switch to new page
    } catch (error) {
      console.error("Failed to add page:", error);
      alert("Failed to add page");
    }
  };

  // Rename page
  const handleRenamePage = async (pageId: string, name: string) => {
    try {
      await dashboardService.updatePage(pageId, name);
      const updated = await dashboardService.get(dashboardId!);
      setDashboard(updated);
    } catch (error) {
      console.error("Failed to rename page:", error);
    }
  };

  // Delete page
  const handleDeletePage = async (pageId: string) => {
    try {
      await dashboardService.deletePage(pageId);
      const updated = await dashboardService.get(dashboardId!);
      setDashboard(updated);
      if (currentPageIndex >= updated.pages.length) {
        setCurrentPageIndex(updated.pages.length - 1);
      }
    } catch (error: any) {
      console.error("Failed to delete page:", error);
      alert(error.response?.data?.error || "Failed to delete page");
    }
  };

  // Rename dashboard
  const handleRenameConfirm = async (name: string, description?: string) => {
    if (!dashboardId) return;

    try {
      await dashboardService.update(dashboardId, { name, description });
      const updated = await dashboardService.get(dashboardId);
      setDashboard(updated);
      setShowRenameModal(false);
    } catch (error: any) {
      console.error("Failed to rename dashboard:", error);
      alert(error.response?.data?.error || "Failed to rename dashboard");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600 dark:text-gray-400">
          Loading dashboard...
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return null;
  }

  // Convert pages to tabs format for TabBar
  const tabs = dashboard.pages.map((page, index) => ({
    id: page.id,
    name: page.name,
    isActive: index === currentPageIndex,
  }));

  return (
    <div className="worksheet">
      {/* Dashboard Toolbar (Hidden in Present Mode) */}
      {!isPresenting && (
        <div className="worksheet-toolbar">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboards")}
              className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              title="Back to dashboard list"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {dashboard.name}
              </h1>
              <button
                onClick={() => setShowRenameModal(true)}
                className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                title="Rename dashboard"
              >
                <Edit2 size={16} />
              </button>
            </div>
            <SaveIndicator status={saveStatus} />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                if (confirm("Delete ALL widgets on this page?")) {
                  const API_URL =
                    import.meta.env.VITE_API_URL || "http://localhost:5173";
                  try {
                    for (const widget of widgets) {
                      await axios.delete(
                        `${API_URL}/api/shell/widgets/${widget.id}`
                      );
                    }
                    const updated = await dashboardService.get(dashboardId!);
                    setDashboard(updated);
                    setSidebarMode({ type: "library" });
                    setSelectedWidgetId(null);
                  } catch (error) {
                    console.error("Failed to clear page:", error);
                  }
                }
              }}
              className="px-3 py-1 text-xs text-white bg-red-600 hover:bg-red-700 rounded-lg"
              title="Clear all widgets from this page"
            >
              Clear Page
            </button>
            <button
              onClick={togglePresenting}
              className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              title="Present Mode (Fullscreen)"
            >
              <Eye size={20} />
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              title="Export to JSON"
            >
              <Download size={20} />
            </button>
            <button
              onClick={() => setShowSettingsModal(true)}
              className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              title="Dashboard Settings"
            >
              <SettingsIcon size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Content Area with Sidebar (matches worksheet-workspace structure) */}
      <div
        className="worksheet-workspace dashboard-workspace-bg"
        style={{ boxShadow: "none" }}
      >
        {/* Canvas Area - Scrollable (Story 2.175: Dynamic height based on widgets) */}
        <div className="worksheet-grid-area dashboard-grid-bg">
          <div
            ref={gridContainerRef}
            className="relative dashboard-canvas-bg"
            style={{
              minHeight: `${rowHeight * 24 + widgetSpacing * 23}px`, // Minimum 24 rows
              height: `${
                rowHeight * canvasHeight + widgetSpacing * (canvasHeight - 1)
              }px`, // Story 2.175: Exact height for content
            }}
            onClick={handleCanvasClick}
          >
            <div
              style={{ height: "100%", position: "relative" }}
              onClick={(e) => {
                // Deselect if clicking empty area
                if (e.target === e.currentTarget) {
                  setSelectedWidgetId(null);
                  setSidebarMode({ type: "library" });
                }
              }}
            >
              {/* Gridline Overlay */}
              <div
                className={`dashboard-grid-overlay ${
                  showGridlines ? "show-gridlines" : ""
                }`}
                style={{
                  height: `${
                    rowHeight * canvasHeight +
                    widgetSpacing * (canvasHeight - 1)
                  }px`,
                }}
              />

              {widgets.length === 0 && <EmptyCanvasState />}

              <ResponsiveGrid
                className="dashboard-grid"
                layouts={{
                  lg: layout,
                  md: layout,
                  sm: layout,
                  xs: layout,
                  xxs: layout,
                }}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
                maxRows={Infinity} // Story 2.175: Unlimited rows, canvas expands dynamically
                rowHeight={rowHeight}
                onLayoutChange={(currentLayout) =>
                  handleLayoutChange(currentLayout)
                }
                isDraggable={!isPresenting} // Disable dragging in present mode
                isResizable={!isPresenting} // Disable resizing in present mode
                draggableHandle=".widget-drag-handle"
                preventCollision={true}
                compactType={null}
                margin={[widgetSpacing, widgetSpacing]}
                isDroppable={!isPresenting} // Disable dropping in present mode
                onDrop={(layout, layoutItem, _event) => {
                  if (draggingWidget && !isPresenting) {
                    handleWidgetDrop(
                      draggingWidget.type,
                      layoutItem.x,
                      layoutItem.y
                    );
                    setDraggingWidget(null);
                  }
                }}
                droppingItem={
                  draggingWidget && !isPresenting
                    ? {
                        i: "__dropping-elem__",
                        w: draggingWidget.w,
                        h: draggingWidget.h,
                      }
                    : undefined
                }
              >
                {widgets.map((widget) => (
                  <div
                    key={widget.id}
                    className={`widget-item ${
                      selectedWidgetId === widget.id
                        ? "ring-2 ring-blue-500"
                        : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleWidgetClick(widget.id, widget.widgetType);
                    }}
                  >
                    <div
                      className="w-full h-full dashboard-widget-themed rounded-lg border flex flex-col relative shadow-sm"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        setHideToolbar((prev) => ({
                          ...prev,
                          [widget.id]: !prev[widget.id],
                        }));
                      }}
                    >
                      {/* Widget Header - ONLY show on selected widget and not hidden */}
                      {selectedWidgetId === widget.id &&
                        !hideToolbar[widget.id] && (
                          <div className="absolute top-0 left-0 right-0 z-10 widget-drag-handle flex items-center justify-between p-2 bg-blue-100/90 dark:bg-blue-900/90 backdrop-blur-sm cursor-move rounded-t-lg">
                            <div className="flex items-center gap-1">
                              <GripVertical
                                size={14}
                                className="text-gray-600 dark:text-gray-400"
                              />
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                {widget.widgetType}
                              </span>
                            </div>
                            <div
                              className="flex gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleWidgetClick(
                                    widget.id,
                                    widget.widgetType
                                  );
                                }}
                                className="p-1 hover:bg-purple-200 dark:hover:bg-purple-800 rounded"
                                title="Edit widget"
                              >
                                <SettingsIcon
                                  size={14}
                                  className="text-gray-700 dark:text-gray-300"
                                />
                              </button>
                              <button
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const API_URL =
                                    import.meta.env.VITE_API_URL ||
                                    "http://localhost:5173";
                                  try {
                                    if (!currentPage) {
                                      alert("No page selected");
                                      return;
                                    }

                                    // Duplicate widget - find next available position
                                    const currentWidget = widgets.find(
                                      (w) => w.id === widget.id
                                    );
                                    if (!currentWidget) {
                                      alert("Widget not found");
                                      return;
                                    }

                                    // Use the smart positioning to find open space (pass actual widget size)
                                    const position = findNextPosition(
                                      currentWidget.widgetType,
                                      {
                                        w: currentWidget.w,
                                        h: currentWidget.h,
                                      }
                                    );

                                    // Check if page is full
                                    if (!position) {
                                      alert(
                                        `No room on this page for another ${currentWidget.widgetType} widget (${currentWidget.w}×${currentWidget.h}). Try adding a new page or removing some widgets.`
                                      );
                                      return;
                                    }

                                    await axios.post(
                                      `${API_URL}/api/shell/pages/${currentPage.id}/widgets`,
                                      {
                                        widgetType: currentWidget.widgetType,
                                        x: position.x,
                                        y: position.y,
                                        w: currentWidget.w,
                                        h: currentWidget.h,
                                        minW: currentWidget.minW,
                                        minH: currentWidget.minH,
                                        config:
                                          typeof currentWidget.config ===
                                          "string"
                                            ? JSON.parse(currentWidget.config)
                                            : currentWidget.config,
                                      }
                                    );
                                    const updated = await dashboardService.get(
                                      dashboardId!
                                    );
                                    setDashboard(updated);
                                  } catch (error: any) {
                                    console.error(
                                      "Failed to duplicate widget:",
                                      error
                                    );
                                    const errorMsg =
                                      error.response?.data?.error ||
                                      error.message ||
                                      "Unknown error";
                                    alert(
                                      `Failed to duplicate widget: ${errorMsg}`
                                    );
                                  }
                                }}
                                className="p-1 hover:bg-blue-200 dark:hover:bg-blue-900 rounded"
                                title="Duplicate widget"
                              >
                                <Copy
                                  size={14}
                                  className="text-gray-700 dark:text-gray-300"
                                />
                              </button>
                              <button
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (confirm("Delete this widget?")) {
                                    const API_URL =
                                      import.meta.env.VITE_API_URL ||
                                      "http://localhost:5173";
                                    try {
                                      await axios.delete(
                                        `${API_URL}/api/shell/widgets/${widget.id}`
                                      );
                                      const updated =
                                        await dashboardService.get(
                                          dashboardId!
                                        );
                                      setDashboard(updated);
                                      setSelectedWidgetId(null);
                                      setSidebarMode({ type: "library" });
                                    } catch (error) {
                                      console.error(
                                        "Failed to delete widget:",
                                        error
                                      );
                                      alert("Failed to delete widget");
                                    }
                                  }
                                }}
                                className="p-1 hover:bg-red-200 dark:hover:bg-red-900 rounded"
                                title="Delete widget"
                              >
                                <X
                                  size={14}
                                  className="text-red-600 dark:text-red-400"
                                />
                              </button>
                            </div>
                          </div>
                        )}

                      {/* Widget Content - No padding, toolbar overlays */}
                      <div className="w-full h-full">
                        <WidgetErrorBoundary
                          widgetId={widget.id}
                          widgetName={widget.widgetType}
                        >
                          {widget.widgetType === "metric" && widget.config ? (
                            <MetricWidget
                              config={
                                typeof widget.config === "string"
                                  ? JSON.parse(widget.config)
                                  : widget.config
                              }
                              width={widget.w}
                              height={widget.h}
                            />
                          ) : widget.widgetType === "chart" && widget.config ? (
                            <ChartWidget
                              config={
                                typeof widget.config === "string"
                                  ? JSON.parse(widget.config)
                                  : widget.config
                              }
                              width={widget.w}
                              height={widget.h}
                            />
                          ) : widget.widgetType === "text" && widget.config ? (
                            <TextWidget
                              config={
                                typeof widget.config === "string"
                                  ? JSON.parse(widget.config)
                                  : widget.config
                              }
                              width={widget.w}
                              height={widget.h}
                              isEditing={selectedWidgetId === widget.id}
                              onChange={async (newConfig) => {
                                // Auto-save text content changes
                                const API_URL =
                                  import.meta.env.VITE_API_URL ||
                                  "http://localhost:5173";
                                try {
                                  await axios.put(
                                    `${API_URL}/api/shell/widgets/${widget.id}`,
                                    {
                                      config: newConfig,
                                    }
                                  );
                                  // Refresh dashboard to show update
                                  const updated = await dashboardService.get(
                                    dashboardId!
                                  );
                                  setDashboard(updated);
                                } catch (error) {
                                  console.error(
                                    "Failed to update text widget:",
                                    error
                                  );
                                }
                              }}
                            />
                          ) : widget.widgetType === "media" && widget.config ? (
                            <MediaWidget
                              config={
                                typeof widget.config === "string"
                                  ? JSON.parse(widget.config)
                                  : widget.config
                              }
                              width={widget.w}
                              height={widget.h}
                            />
                          ) : widget.widgetType === "iframe" &&
                            widget.config ? (
                            <IFrameWidget
                              config={
                                typeof widget.config === "string"
                                  ? JSON.parse(widget.config)
                                  : widget.config
                              }
                              width={widget.w}
                              height={widget.h}
                            />
                          ) : widget.widgetType === "table" && widget.config ? (
                            <TableWidget
                              config={
                                typeof widget.config === "string"
                                  ? JSON.parse(widget.config)
                                  : widget.config
                              }
                              width={widget.w}
                              height={widget.h}
                            />
                          ) : (
                            <div className="text-center p-4">
                              <p className="text-gray-400 dark:text-gray-500 text-sm">
                                {widget.widgetType} widget
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {widget.w}×{widget.h}
                              </p>
                            </div>
                          )}
                        </WidgetErrorBoundary>
                      </div>
                    </div>
                  </div>
                ))}
              </ResponsiveGrid>
            </div>
          </div>
        </div>

        {/* Sidebar - RIGHT SIDE within content area (Hidden in Present Mode) */}
        {!isPresenting && !sidebarCollapsed && (
          <div className="flex-shrink-0 overflow-y-auto dashboard-themed">
            {sidebarMode.type === "library" ? (
              <WidgetLibrarySidebar
                onWidgetDrop={handleWidgetDrop}
                onCollapse={() => setSidebarCollapsed(true)}
                onDragStart={(type, w, h) => {
                  setDraggingWidget({ type, w, h });
                }}
                onDragEnd={() => {
                  setDraggingWidget(null);
                }}
                customWidgets={CUSTOM_WIDGETS}
              />
            ) : (
              <WidgetConfigSidebar
                widgetId={sidebarMode.widgetId}
                widgetType={sidebarMode.widgetType}
                currentConfig={(() => {
                  const w = widgets.find((w) => w.id === sidebarMode.widgetId);
                  if (!w || !w.config) return {};
                  return typeof w.config === "string"
                    ? JSON.parse(w.config)
                    : w.config;
                })()}
                onDone={handleConfigDone}
                onDelete={handleWidgetDelete}
                onCollapse={() => setSidebarCollapsed(true)}
                onConfigChange={handleConfigChange}
              />
            )}
          </div>
        )}

        {/* Expand Sidebar Button (when collapsed, Hidden in Present Mode) */}
        {!isPresenting && sidebarCollapsed && (
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="fixed right-0 top-1/2 -translate-y-1/2 p-3 bg-green-600 hover:bg-green-700 text-white rounded-l-lg shadow-lg z-10"
            title="Show sidebar"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}
      </div>

      {/* TabBar for Pages at bottom (Hidden in Present Mode) - matches grid system */}
      {!isPresenting && (
        <TabBar
          tabs={tabs}
          onTabClick={(tabId) => {
            const pageIndex = dashboard.pages.findIndex((p) => p.id === tabId);
            if (pageIndex !== -1) setCurrentPageIndex(pageIndex);
          }}
          onTabRename={(tabId, newName) => {
            handleRenamePage(tabId, newName);
          }}
          onTabClose={(tabId) => {
            if (dashboard.pages.length > 1) {
              handleDeletePage(tabId);
            }
          }}
          onAddTab={handleAddPage}
        />
      )}

      {/* Export Modal (Story 2.19) */}
      {showExportModal && dashboard && (
        <ExportModal
          dashboard={dashboard}
          userId={dashboard.userId}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {/* Settings Modal (Story 2.21) */}
      {showSettingsModal && dashboard && (
        <DashboardSettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          dashboardId={dashboard.id}
          dashboardName={dashboard.name}
        />
      )}

      {/* Rename Modal */}
      {showRenameModal && dashboard && (
        <RenameDashboardModal
          currentName={dashboard.name}
          currentDescription={dashboard.description}
          onConfirm={handleRenameConfirm}
          onCancel={() => setShowRenameModal(false)}
        />
      )}

      {/* Present Mode Floating Navigation */}
      {isPresenting && dashboard && (
        <PresentModeNavigation
          pages={dashboard.pages}
          currentPageIndex={currentPageIndex}
          onPageChange={setCurrentPageIndex}
          onExit={togglePresenting}
        />
      )}
    </div>
  );
}
